import { PlusIcon, TrashIcon } from "@heroicons/react/outline";
import { useEffect, useState } from "react";

import { SignalHookFormState, SignalHookScopeType } from "../types";
import { getHookSummary, getRedundantHookIds } from "../utils";

import HookCollapsibleSection from "./HookCollapsibleSection";
import HookRow from "./HookRow";

import { Button } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";

interface ScopeProps {
  hooks: SignalHookFormState[];
  onHookChange: (hookId: string, hook: SignalHookFormState) => void;
  onAddHook: () => void;
  onRemoveHook: (hookId: string) => void;
}

const ADD_HOOK_DISABLED_TOOLTIP = "Switch away from All requests to add another hook.";
const REDUNDANT_HOOK_MESSAGE =
  "Other hooks already match these requests. Change or remove this hook.";

const Scope = ({ hooks, onHookChange, onAddHook, onRemoveHook }: ScopeProps) => {
  const [expandedHookIds, setExpandedHookIds] = useState<string[]>(() =>
    hooks.map(hook => hook.id),
  );

  useEffect(() => {
    setExpandedHookIds(current => {
      const hookIds = new Set(hooks.map(hook => hook.id));
      const next = current.filter(id => hookIds.has(id));

      for (const hook of hooks) {
        if (!next.includes(hook.id)) {
          next.push(hook.id);
        }
      }

      return next;
    });
  }, [hooks]);

  const hasAllRequestsHook = hooks.some(
    hook => hook.scopeType === SignalHookScopeType.ALL_REQUESTS,
  );
  const disableAllRequests = hooks.length > 1;
  const redundantHookIds = getRedundantHookIds(hooks);
  const showCollapseControls = hooks.length > 1;

  const addHookButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={hasAllRequestsHook}
      onClick={onAddHook}
    >
      <PlusIcon className="mr-2 h-4 w-4" />
      Add hook
    </Button>
  );

  return (
    <section className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Scope</h2>
          <p className="mt-1 text-sm text-gray-500">
            Choose which requests are eligible and what percentage to evaluate.
          </p>
        </div>
        {showCollapseControls ? (
          <div className="flex shrink-0 items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-sm text-gray-600"
              onClick={() => setExpandedHookIds([])}
            >
              Collapse all
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-sm text-gray-600"
              onClick={() => setExpandedHookIds(hooks.map(hook => hook.id))}
            >
              Expand all
            </Button>
          </div>
        ) : null}
      </div>

      <div className="space-y-4">
        {hooks.map((hook, index) => (
          <HookCollapsibleSection
            key={hook.id}
            title={getHookSummary(hook, index)}
            isExpanded={expandedHookIds.includes(hook.id)}
            onExpandedChange={expanded => {
              setExpandedHookIds(current =>
                expanded
                  ? Array.from(new Set([...current, hook.id]))
                  : current.filter(id => id !== hook.id),
              );
            }}
            headerAction={
              hooks.length > 1 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-500 hover:text-gray-700"
                  aria-label="Remove hook"
                  onClick={event => {
                    event.stopPropagation();
                    onRemoveHook(hook.id);
                  }}
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              ) : undefined
            }
          >
            {redundantHookIds.has(hook.id) ? (
              <p className="text-sm text-red-600">{REDUNDANT_HOOK_MESSAGE}</p>
            ) : null}
            <HookRow
              hook={hook}
              disableAllRequests={disableAllRequests}
              onChange={nextHook => onHookChange(hook.id, nextHook)}
            />
          </HookCollapsibleSection>
        ))}
      </div>

      {hasAllRequestsHook ? (
        <BasicTooltip content={ADD_HOOK_DISABLED_TOOLTIP}>
          <span className="inline-flex">{addHookButton}</span>
        </BasicTooltip>
      ) : (
        addHookButton
      )}
    </section>
  );
};

export default Scope;
